import React, { FC, useState } from 'react';
import { useLicenses } from '~Hooks';
import AccessBlocked from '../AccessBlocked';
import { BAEMINGO_PRODUCT } from '../../../../../__queries__';
import ClaimLicense from '../ClaimLicense';
import InvoiceWarning from '../InvoiceWarning';
import { useStorage } from '~Components/Storage';
import { useDeviceHandler } from '~Components/DeviceProvider';

/** Maps URL paths to required product licenses */
const URL_TO_DEVICE_PRODUCT_MAP: Record<string, BAEMINGO_PRODUCT> = {
  // TSR (Table Service) - tables
  tables: BAEMINGO_PRODUCT.TSR,

  // QSR (Quick Service) - pos, multi-pos, parked-orders
  pos: BAEMINGO_PRODUCT.QSR,
  'multi-pos': BAEMINGO_PRODUCT.QSR,
  'parked-orders': BAEMINGO_PRODUCT.QSR,

  // KDS (Kitchen Display System)
  'kitchen-display': BAEMINGO_PRODUCT.KDS,
  'evented-kitchen-display': BAEMINGO_PRODUCT.KDS,

  // EPOS
  'self-service-kiosks': BAEMINGO_PRODUCT.EPOS,

  // Customer Display
  'customer-display-responsive': BAEMINGO_PRODUCT.CUSTOMER_DISPLAY,
  'customer-display': BAEMINGO_PRODUCT.CUSTOMER_DISPLAY,
  'order-ready-display': BAEMINGO_PRODUCT.CUSTOMER_DISPLAY,
};

const ALWAYS_ALLOWED_PATHS = [
  'gateway',
  'history',
  'newReports',
  'z-reports',
  'settings-v2',
  'handy',
];

/** License-related: Handles conditions if we need to show a modal to block access to certain product (not including handy here) */
const AccessHandler: FC = () => {
  const licenses = useLicenses();
  const deviceHandler = useDeviceHandler();
  const [storage, , storageLoading] = useStorage();

  const [isInvoiceWarningDismissed, setIsInvoiceWarningDismissed] = useState<boolean>(false);

  const pathName = location.hash;

  const isAlwaysAllowedPath = ALWAYS_ALLOWED_PATHS.some((path) => pathName.includes(path));

  const missingTaxComplianceInfo =
    !storageLoading && (!storage.taxComplianceDeviceId || !storage.taxComplianceDeviceName);

  if (licenses.isPOSAccessBlocked && !isAlwaysAllowedPath) {
    return <AccessBlocked />;
  }

  if (licenses.isInvoiceWarningActive && !isInvoiceWarningDismissed) {
    return <InvoiceWarning onComplete={() => setIsInvoiceWarningDismissed(true)} />;
  }

  /**
   * If this is true, we show the onboarding modal in src/Scenes/Onboarding/index.tsx
   * So we stop here so we don't potentially trigger two conflicting modals
   */
  if (!deviceHandler.activeDevice || missingTaxComplianceInfo) {
    return null;
  }

  let matchedProduct: BAEMINGO_PRODUCT | undefined;

  // Check if the path name includes any of the keys in the URL_TO_DEVICE_PRODUCT_MAP
  for (const [urlPath, product] of Object.entries(URL_TO_DEVICE_PRODUCT_MAP)) {
    if (pathName.includes(urlPath)) {
      matchedProduct = product;
      break;
    }
  }

  /*
   * Here we check if device doesn't have license but there is an available to claim
   * For handy we do it in its own Scene
   */
  switch (matchedProduct) {
    case BAEMINGO_PRODUCT.TSR:
      if (!licenses.hasTSR && licenses.isTSRClaimable) {
        return <ClaimLicense product={BAEMINGO_PRODUCT.TSR} />;
      }
      break;

    case BAEMINGO_PRODUCT.QSR:
      if (!licenses.hasQSR && licenses.isQSRClaimable) {
        return <ClaimLicense product={BAEMINGO_PRODUCT.QSR} />;
      }
      break;

    case BAEMINGO_PRODUCT.KDS:
      if (!licenses.hasKDS && licenses.isKDSClaimable) {
        return <ClaimLicense product={BAEMINGO_PRODUCT.KDS} />;
      }
      break;

    case BAEMINGO_PRODUCT.EPOS:
      if (!licenses.hasEPOS && licenses.isEPOSClaimable) {
        return <ClaimLicense product={BAEMINGO_PRODUCT.EPOS} />;
      }
      break;

    case BAEMINGO_PRODUCT.CUSTOMER_DISPLAY:
      if (!licenses.hasCustomerDisplay && licenses.isCustomerDisplayClaimable) {
        return <ClaimLicense product={BAEMINGO_PRODUCT.CUSTOMER_DISPLAY} />;
      }
      break;

    default:
      break;
  }

  /* Device is allowed to enter since it has license for it already & has their billing paid for */
  return null;
};

export default AccessHandler;
